import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'

import {
  REACTIVATE_USER_SUBSCRIPTION_KEY,
  reactivateUserSubscription,
} from '@/actions/settings/billingActions'
import { sendUserCancellationMessageZap } from '@/actions/zapierActions'
import { selectHeardProductSubscription } from '@/reducers/subscription.slice'
import { selectErrorsForKeys } from '@/reducers/fetch'
import { getCurrentUser } from '@/selectors/user.selectors'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { Alert, Text, Button, Dialog } from '@/ui'
import { TypographyStyles } from '@/styles'

export const ReactivatePlanDialog = ({
  redirectUrl,
}: {
  redirectUrl?: string
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const subscription = useReselector(selectHeardProductSubscription)
  const [disabled, setDisabled] = useState(false)
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const currentUser = useReselector(getCurrentUser)

  const error = useReselector(selectErrorsForKeys, [
    REACTIVATE_USER_SUBSCRIPTION_KEY,
  ])

  useEffect(() => {
    pageView('reactivate plan modal')
  }, [pageView])

  const reactivatePlan = async () => {
    setDisabled(true)
    track('submit reactivate plan request')

    const result = await reactivateUserSubscription()(dispatch)
    setDisabled(false)
    if (!result) {
      return
    }

    sendUserCancellationMessageZap(
      currentUser,
      'reactivated_cancelling_account'
    )

    if (redirectUrl) {
      navigate(redirectUrl)
    } else {
      location.reload()
    }
  }

  const nextBillingDate =
    subscription?.nextBillingDate &&
    moment.unix(subscription.nextBillingDate).format(DATE_FORMATS.DISPLAY_LONG)

  return (
    <Dialog>
      <Dialog.Trigger asChild>
        <Button
          variant="primary"
          onClick={() => {
            track('click reactivate plan')
          }}
        >
          Reactivate Plan
        </Button>
      </Dialog.Trigger>

      <Dialog.Content>
        <Dialog.Title>Reactivate your Heard Plan</Dialog.Title>

        {error.length > 0 && <Alert variant="error">{error[0].message}</Alert>}

        <Text variant={TypographyStyles.BODY_LG}>
          You have full access to Heard until your billing cycle ends on{' '}
          {nextBillingDate}.
        </Text>

        <Text variant={TypographyStyles.BODY_LG}>
          Reactivating your Heard plan means that you will be charged on your
          next billing date.
        </Text>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary">Close</Button>
          </Dialog.Close>

          <Button
            variant="primary"
            onClick={reactivatePlan}
            disabled={disabled}
          >
            Reactivate Plan
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
